// user-dashboard/components/UnmatchedPortsTable.jsx
import React, { useState } from "react";
import Secret from "./Secret";
import api, {
  updateMyGatewayPort,
  createAdvicePasswordMe,
  updateAdvicePasswordMe,
  fetchAdvicePasswordsMe,
} from "../../../api";
import { isSimConvertedYes, shouldShowActivate } from "../../../shared/portActivation";

/**
 * Returns true if this port row should be highlighted as a duplicate.
 * Checks whether the port's email OR its assigned number appears elsewhere.
 */
function isDuplicatePort(port, duplicateEmails, duplicateNumbers) {
  const email = (port.user_email || "").trim().toLowerCase();
  if (email && duplicateEmails.has(email)) return true;

  const num = String(port.assigned_number || "").replace(/\D/g, "");
  if (num && duplicateNumbers.has(num)) return true;

  return false;
}

function adviceForPort(port, advice) {
  const key = (port?.user_email || "").trim().toLowerCase();
  if (!key) return null;
  return (
    (advice || []).find(
      (a) => (a.email || "").trim().toLowerCase() === key
    ) || null
  );
}

/**
 * UnmatchedPortsTable
 *
 * Shows ports that are not linked to any advice email.
 * Rows with a duplicate email or phone number are highlighted in red.
 */
export default function UnmatchedPortsTable({
  unmatchedPorts,
  onActivatePort,
  getButtonState,
  getStatusBadge,
  portStatus,
  portMessages,
  portCountdowns,
  clearPortMessage,
  onPortUpdate, // Callback to refresh ports after update
  onAdviceUpdate, // Callback to refresh advice passwords after update
  duplicateEmails = new Set(),
  duplicateNumbers = new Set(),
  portsByEmail = new Map(),
  advice = [],
}) {
  const [editingPortId, setEditingPortId] = useState(null);
  const [editDraft, setEditDraft] = useState({ site: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const startEdit = (port) => {
    setEditingPortId(port.id);
    const email = port.user_email || "";
    const existing = adviceForPort({ ...port, user_email: email }, advice);
    setEditDraft({
      site: existing?.site || "",
      email: email,
      password: "",
    });
    setError(null);
  };

  const cancelEdit = () => {
    setEditingPortId(null);
    setEditDraft({ site: "", email: "", password: "" });
    setError(null);
  };

  const saveEdit = async (port) => {
    if (!editDraft.email) {
      setError("Email is required");
      return;
    }

    // Check if this port's number is already linked to a different email
    const portNum = String(port.assigned_number || "").replace(/\D/g, "");
    if (portNum) {
      const newEmailKey = editDraft.email.trim().toLowerCase();
      let usedByOtherEmail = false;
      for (const [emailKey, emailPorts] of portsByEmail.entries()) {
        if (emailKey === newEmailKey) continue;
        if (emailPorts.some(p => String(p.assigned_number || "").replace(/\D/g, "") === portNum)) {
          usedByOtherEmail = true;
          break;
        }
      }
      if (usedByOtherEmail) {
        const confirmed = window.confirm(
          `⚠️ ${port.assigned_number} is already linked to a different email.\n\nAre you sure you want to also assign it to ${editDraft.email}?`
        );
        if (!confirmed) return;
      }
    }

    try {
      setSaving(true);
      setError(null);

      // Service numbers have no gateway — still persist user_email on the port
      // so they link to the advice row (email/password).
      await updateMyGatewayPort(port.id, {
        user_email: editDraft.email,
      });

      // Check if advice password exists for this email
      const adviceList = await fetchAdvicePasswordsMe().catch(() => []);
      const emailKey = editDraft.email.trim().toLowerCase();
      const existingAdvice = adviceList.find(
        (a) => (a.email || "").trim().toLowerCase() === emailKey
      );

      if (existingAdvice) {
        // Update existing advice password
        const updateData = {};
        if (editDraft.site !== undefined && editDraft.site !== "") {
          updateData.site = editDraft.site;
        }
        if (editDraft.password !== undefined && editDraft.password !== "") {
          updateData.password = editDraft.password;
        }
        if (Object.keys(updateData).length > 0) {
          await updateAdvicePasswordMe(existingAdvice.id, updateData);
        }
      } else {
        // Create new advice password
        await createAdvicePasswordMe({
          site: editDraft.site || "",
          email: editDraft.email,
          password: editDraft.password || "",
          main: false,
        });
      }

      // Refresh data
      if (onPortUpdate) await onPortUpdate();
      if (onAdviceUpdate) await onAdviceUpdate();

      cancelEdit();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-5">
      <h5>Other Numbers</h5>
      {error && (
        <div className="alert alert-danger alert-sm py-2 mb-2">
          <small>{error}</small>
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-striped table-bordered align-middle">
          <thead>
            <tr>
              <th style={{ width: 140 }}>Port</th>
              <th style={{ width: 180 }}>Phone Number &amp; Status</th>
              <th style={{ width: 150 }}>Site</th>
              <th style={{ width: 200 }}>Email</th>
              <th style={{ width: 180 }}>Password</th>
              <th style={{ width: 160 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {unmatchedPorts.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-muted">
                  None — all of your numbers are already shown above by email.
                </td>
              </tr>
            ) : (
              unmatchedPorts.map((p) => {
                const buttonState = getButtonState(p);
                const isEditing = editingPortId === p.id;
                const isDuplicate = isDuplicatePort(p, duplicateEmails, duplicateNumbers);
                const linkedAdvice = adviceForPort(p, advice);

                return (
                  <tr
                    key={p.id}
                    style={
                      isDuplicate
                        ? { backgroundColor: "#ffe0e0", borderLeft: "4px solid #dc3545" }
                        : undefined
                    }
                    title={isDuplicate ? "⚠ Duplicate email or phone number detected" : undefined}
                  >
                    <td>
                      <span className="badge bg-light text-dark">
                        {p.port_number || "—"}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-2">
                        <span>{p.assigned_number || "—"}</span>
                        {getStatusBadge(p)}
                      </div>
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          className="form-control form-control-sm"
                          value={editDraft.site}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, site: e.target.value })
                          }
                          placeholder="Site name"
                        />
                      ) : (
                        <span className="text-muted">
                          {linkedAdvice?.site || "—"}
                        </span>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <input
                          className="form-control form-control-sm"
                          type="email"
                          value={editDraft.email}
                          onChange={(e) =>
                            setEditDraft({ ...editDraft, email: e.target.value })
                          }
                          placeholder="email@example.com"
                          required
                        />
                      ) : (
                        <span className="text-muted">{p.user_email || "—"}</span>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <Secret
                          value={editDraft.password}
                          onChange={(v) =>
                            setEditDraft({ ...editDraft, password: v })
                          }
                          editable
                        />
                      ) : linkedAdvice?.password ? (
                        <Secret value={linkedAdvice.password} />
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {isEditing ? (
                        <div className="d-flex gap-1">
                          <button
                            className="btn btn-sm btn-success"
                            onClick={() => saveEdit(p)}
                            disabled={saving}
                          >
                            {saving ? "Saving..." : "Save"}
                          </button>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={cancelEdit}
                            disabled={saving}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex gap-1">
                          {shouldShowActivate(p) && !buttonState.hide && (
                            <button
                              className={buttonState.className}
                              onClick={() => onActivatePort(p)}
                              disabled={buttonState.disabled}
                              title={buttonState.title}
                              style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                            >
                              {buttonState.text}
                            </button>
                          )}
                          {(!shouldShowActivate(p) || buttonState.hide) && (
                            <span
                              className="badge bg-secondary"
                              title={isSimConvertedYes(p) ? "SIM already converted" : "Service number"}
                              style={{ fontSize: "0.7rem", alignSelf: "center" }}
                            >
                              {isSimConvertedYes(p) ? "Converted" : "Service"}
                            </span>
                          )}
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => startEdit(p)}
                            title="Add email, site, and password"
                          >
                            ✏️ Edit
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
